import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/discovery-calls`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "" });
      toast.success("Received. We'll be in touch shortly.");
    } catch (err) {
      setStatus("error");
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      toast.error(typeof msg === "string" ? msg : "Please check the form.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0a0705] py-32 md:py-48"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-12 gap-y-16 px-6 md:px-10">
        <div className="col-span-12 md:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="overline"
          >
            <span className="text-[#d99036]">✦</span>&nbsp;&nbsp;Contact
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-display text-6xl font-black leading-[0.92] tracking-tight md:text-8xl"
          >
            Let&rsquo;s build
            <br />
            <span className="font-serif-editorial italic font-normal text-[#d99036]">
              something.
            </span>
          </motion.h2>
          <p className="mt-8 max-w-md font-serif-editorial text-xl leading-relaxed text-[#f2ebdd] md:text-2xl">
            If you&rsquo;ve made it this far, you already know whether we&rsquo;re the right fit.
          </p>
          <div className="mt-12 space-y-6 border-t border-[#2a1f17] pt-8">
            <div>
              <p className="overline">Email</p>
              <a
                href="mailto:biz@butterhoneylabs.com"
                data-testid="contact-email"
                className="btn-underline mt-2 inline-block font-display text-xl text-[#f9f6f0] md:text-2xl"
              >
                biz@butterhoneylabs.com
              </a>
            </div>
            <div>
              <p className="overline">Studio</p>
              <p className="mt-2 font-body text-sm text-[#a89f91]">
                Butter Honey Labs — A creative house building brands that last.
              </p>
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          className="col-span-12 flex flex-col gap-8 md:col-span-6 md:col-start-7"
        >
          <FormField
            label="Your Name"
            id="name"
            value={form.name}
            onChange={setField("name")}
            testId="contact-name-input"
            placeholder="Jane Doe"
          />
          <FormField
            label="Email"
            id="email"
            type="email"
            value={form.email}
            onChange={setField("email")}
            testId="contact-email-input"
            placeholder="jane@studio.com"
          />
          <FormField
            label="Brand / Company"
            id="company"
            value={form.company}
            onChange={setField("company")}
            testId="contact-company-input"
            placeholder="Optional"
          />
          <div className="flex flex-col">
            <label
              htmlFor="message"
              className="overline mb-4 text-[#a89f91]"
            >
              Tell us about the brief
            </label>
            <textarea
              id="message"
              data-testid="contact-message-input"
              value={form.message}
              onChange={setField("message")}
              rows={5}
              placeholder="A few lines about your brand, ambitions, timing…"
              className="resize-none border-b border-[#2a1f17] bg-transparent pb-3 pt-1 font-body text-lg text-[#f9f6f0] placeholder:text-[#a89f91]/50 focus:border-[#d99036] focus:outline-none"
              style={{ transition: "border-color 400ms ease" }}
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-btn"
            disabled={status === "submitting"}
            className="group mt-4 inline-flex items-center justify-between border border-[#d99036] bg-[#d99036]/10 px-8 py-6 font-display text-sm uppercase tracking-[0.28em] text-[#f9f6f0] transition-[background-color,color,border-color] duration-500 hover:bg-[#d99036] hover:text-[#0a0705] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span>
              {status === "submitting"
                ? "Sending…"
                : status === "success"
                  ? "Received — thank you"
                  : "Book a Discovery Call"}
            </span>
            <span
              aria-hidden
              className="inline-block transition-transform duration-500 group-hover:translate-x-2"
            >
              →
            </span>
          </button>
        </form>
      </div>
    </section>
  );
}

const FormField = ({ label, id, value, onChange, testId, type = "text", placeholder }) => (
  <div className="flex flex-col">
    <label htmlFor={id} className="overline mb-4 text-[#a89f91]">
      {label}
    </label>
    <input
      id={id}
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="border-b border-[#2a1f17] bg-transparent pb-3 pt-1 font-body text-lg text-[#f9f6f0] placeholder:text-[#a89f91]/50 focus:border-[#d99036] focus:outline-none"
      style={{ transition: "border-color 400ms ease" }}
    />
  </div>
);
